<script setup lang="ts">
import {
	addressCues,
	booleanTags,
	contexts,
	countTriggers,
	freeTextTriggers,
	gaps,
	nlTriggers,
	traps,
	uiControls,
	units,
	vocabularies,
	type HelpGap,
} from "@/lib/voiceHelp";

/**
 * /_/voice/help — the customer-facing guide to saying a session out loud for the
 * Voice workspace. It opens in its own window from the workspace toolbar.
 *
 * Every vocabulary row, trigger row, trap and gap comes from @/lib/voiceHelp,
 * which is the single source that mirrors the engine, so the guide cannot drift
 * from what is actually recognized; no value list is re-typed here. The only
 * literals on the page are the worked example (verified against the engine) and
 * the pane / colour facts read out of ts/view/Voice.vue.
 */

// Colour facts mirrored from ts/view/Voice.vue's scoped styles: the tint each
// transcript line takes from its context, and the hue each extraction-row type
// paints with.
const contextColours = [
	{ name: "access", colour: "#e3f2fd" },
	{ name: "facilitator", colour: "#f3e5f5" },
	{ name: "feature", colour: "#e8f5e9" },
	{ name: "inspection", colour: "#fff8e1" },
	{ name: "mitigator", colour: "#fce4ec" },
	{ name: "safety", colour: "#e0f7fa" },
	{ name: "site", colour: "#efebe9" },
	{ name: "treatment", colour: "#ede7f6" },
	{ name: "no context", colour: "#f8fafc" },
];

// One hue per extraction-row type; the rows paint with
// `hsl(var(--type-hue) 70% 96%)`.
const rowHues = [
	{ type: "boolean", hue: 210 },
	{ type: "enumeration", hue: 145 },
	{ type: "number", hue: 25 },
	{ type: "free text", hue: 265 },
	{ type: "negation", hue: 0 },
	{ type: "meaning", hue: 320 },
	{ type: "measurement", hue: 190 },
	{ type: "address", hue: 90 },
];

const panes = [
	{
		name: "Live transcript",
		shows:
			"One paragraph per utterance, with pause markers and a coloured tag per line for the context it sits in. Hover or focus a word to see the fact it recorded.",
	},
	{
		name: "Prompts",
		shows:
			"The symbolic prompts for the session; the first is what the workspace would hear next. Headset mode speaks it when a pause is detected.",
	},
	{
		name: "Extractions",
		shows:
			"One row per fact read from the transcript: type, key and value. The row colour follows the fact type.",
	},
	{
		name: "Tags",
		shows: "One pill per custom tag captured by a tag phrase.",
	},
	{
		name: "Knowledge graph",
		shows:
			"The session tree: each context with its attributes (✓ present, ✕ not present, ? unknown) and the free text left over inside it.",
	},
	{
		name: "Inferences + Effects",
		shows:
			"The understanding read, the gaps and implied facts, then the procedural effects proposed from the graph.",
	},
];

// The gap section is grouped by how far each declared area actually got.
const gapGroups = (
	[
		{ status: "wired", label: "Wired", badge: "text-bg-success" },
		{ status: "partial", label: "Partial", badge: "text-bg-warning" },
		{
			status: "declared-only",
			label: "Declared only",
			badge: "text-bg-secondary",
		},
	] satisfies { status: HelpGap["status"]; label: string; badge: string }[]
)
	.map((group) => ({
		...group,
		entries: gaps.filter((gap) => gap.status === group.status),
	}))
	.filter((group) => group.entries.length > 0);
</script>

<template>
	<div class="voice-help container py-4">
		<!-- ── The guide. Complete on its own: the sections below it are
		     reference material only. ──────────────────────────────────── -->
		<header class="guide-head">
			<h1>Voice guide</h1>
			<p class="purpose">
				How to say a visit out loud so the Voice workspace records it as facts.
				Everything down to the traps is the guide; the folded sections below it
				are reference.
			</p>
			<RouterLink class="back-link" to="/_/voice">
				<i class="bi bi-arrow-left" aria-hidden="true"></i>
				Back to the Voice workspace
			</RouterLink>
		</header>

		<h2 class="section-title">The one rule</h2>
		<p class="rule">Every fact has to sit inside a context span.</p>
		<ul class="corollaries">
			<li>
				A span opens with <code>begin &lt;name&gt;</code> or one of the
				natural-language openers, and closes with
				<code>end &lt;name&gt;</code> — or simply by starting the next context,
				which closes the one before it. Only one context is open at a time, and
				a span left open runs to the end of the session.
			</li>
			<li>
				Say the same field twice inside one span and the <strong>last</strong>
				value wins.
			</li>
			<li>
				Matching is exact stem equality. Case never matters, and a plural or an
				inflection of a listed word still matches — but
				<strong>synonyms never match</strong>, so say the listed word.
			</li>
			<li>
				Every value and every trigger in the tables on this page has to be said
				as written; an inflection of it is fine.
			</li>
		</ul>

		<h2 class="section-title">The two ways to speak a span</h2>
		<ol class="ways">
			<li>
				<strong>Explicit markers.</strong> Wrap the facts and name the context:
				<code>begin &lt;context&gt; … end &lt;context&gt;</code>.
			</li>
			<li>
				<strong>A natural-language opener.</strong> The phrases in the table
				below open their context on their own, and some of them state a fact at
				the same time.
			</li>
		</ol>

		<p class="template">
			begin &lt;context&gt;
			<span class="slot">name value</span>
			<span class="slot">tag</span>
			<span class="slot">amount 2 ounces</span>
			<span class="slot">tag as &lt;words&gt;</span>
			end &lt;context&gt;
		</p>

		<table class="help-table">
			<thead>
				<tr>
					<th scope="col">Phrase</th>
					<th scope="col">What it fires</th>
					<th scope="col">Example utterance</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="trigger in nlTriggers" :key="trigger.phrase">
					<td class="phrase">{{ trigger.phrase }}</td>
					<td>{{ trigger.fires }}</td>
					<td class="example">{{ trigger.example }}</td>
				</tr>
			</tbody>
		</table>

		<h2 class="section-title">Free text</h2>
		<p>
			Some fields keep the words that follow them, as written, instead of
			matching a value. Say the phrase inside its own context and the rest of
			the sentence is kept until the span ends.
		</p>
		<table class="help-table">
			<thead>
				<tr>
					<th scope="col">Phrase</th>
					<th scope="col">What it keeps</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="trigger in freeTextTriggers" :key="trigger.phrase">
					<td class="phrase">{{ trigger.phrase }}</td>
					<td>{{ trigger.fires }}</td>
				</tr>
			</tbody>
		</table>

		<h2 class="section-title">The nine contexts</h2>
		<table class="help-table">
			<thead>
				<tr>
					<th scope="col">Context</th>
					<th scope="col">What it is for</th>
					<th scope="col">What you can state</th>
					<th scope="col">Example utterance</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="context in contexts" :key="context.name">
					<td class="phrase">
						<code>{{ context.name }}</code>
					</td>
					<td>{{ context.purpose }}</td>
					<td>
						<template v-if="context.fields.length">
							<span v-for="field in context.fields" :key="field" class="pill">{{
								field
							}}</span>
						</template>
						<span v-else class="note">—</span>
					</td>
					<td class="example">{{ context.example }}</td>
				</tr>
			</tbody>
		</table>

		<h2 class="section-title">Sizes and counts</h2>
		<table class="help-table">
			<thead>
				<tr>
					<th scope="col">Trigger</th>
					<th scope="col">What it records</th>
					<th scope="col">Example utterance</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="trigger in countTriggers" :key="trigger.phrase">
					<td class="phrase">{{ trigger.phrase }}</td>
					<td>{{ trigger.fires }}</td>
					<td class="example">{{ trigger.example }}</td>
				</tr>
			</tbody>
		</table>
		<p class="units">
			Units:
			<span v-for="unit in units" :key="unit" class="pill">{{ unit }}</span>
		</p>
		<p class="note">
			Counts are spoken as words or digits. How a visit was actually carried out
			— the dips a survey took, the procedure at each station — is not a count:
			say <code>method</code> inside an <code>inspection</code> span and the
			rest of the sentence is captured as written.
		</p>

		<h2 class="section-title">A worked example</h2>
		<p>
			Three spoken lines. Every fact below them was read out of the utterance
			above it.
		</p>
		<div
			class="transcript"
			role="figure"
			aria-label="Worked example transcript"
		>
			<p class="line ctx-feature">
				<span class="context-tag">feature</span>I'm in the back yard and there
				is a green pool; it looks about ten by fifteen by three feet.
			</p>
			<p class="line ctx-inspection">
				<span class="context-tag">inspection</span>I am making an inspection,
				density high, stage mixed.
			</p>
			<p class="line ctx-treatment">
				<span class="context-tag">treatment</span>I made a treatment with
				VectoMax FG, two ounces.
			</p>
		</div>
		<ol class="facts">
			<li>
				<code>green pool</code> opens a <code>feature</code> span and states the
				habitat is a swimming pool with maintenance false; it deliberately
				leaves the word <code>pool</code> alone so the size grammar can read the
				dimensions that follow.
			</li>
			<li>
				<code>about ten by fifteen by three feet</code> is a size specification:
				three dimensions give a volume (two give a surface area); feet is
				assumed when no unit is spoken.
			</li>
			<li>
				<code>make an inspection</code> opens an <code>inspection</code> span;
				<code>density high</code> → density = High; <code>stage mixed</code> →
				stage = Mixed.
			</li>
			<li>
				<code>make a treatment</code> opens a <code>treatment</code> span;
				naming a product asserts the product, and a trailing amount gives the
				quantity (<code>amount = 2 ounces</code>). Every product name separates
				into its own treatment span.
			</li>
			<li>
				The next opener closes the previous span — you never have to close the
				first one. Use <code>begin &lt;name&gt; … end &lt;name&gt;</code> when
				you want explicit control.
			</li>
			<li>
				Add your own label with <code>tag as &lt;words&gt;</code>; tags land in
				the Tags pane.
			</li>
		</ol>

		<h2 class="section-title">The traps to know before you record</h2>
		<table class="help-table">
			<thead>
				<tr>
					<th scope="col">What you said</th>
					<th scope="col">Why nothing was recorded</th>
					<th scope="col">Say instead</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="trap in traps" :key="trap.what">
					<td class="phrase">{{ trap.what }}</td>
					<td>{{ trap.why }}</td>
					<td>{{ trap.instead }}</td>
				</tr>
			</tbody>
		</table>

		<!-- ── Reference. Everything below folds away. ─────────────────── -->
		<details class="reference">
			<summary>
				<h2 class="section-title">Value lists (reference)</h2>
			</summary>
			<div class="reference-body">
				<h3>Fields and their values</h3>
				<table class="help-table">
					<thead>
						<tr>
							<th scope="col">Context</th>
							<th scope="col">Field</th>
							<th scope="col">Values</th>
							<th scope="col">Note</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="entry in vocabularies"
							:key="`${entry.context}.${entry.key}`"
						>
							<td class="phrase">
								<code>{{ entry.context }}</code>
							</td>
							<td class="phrase">{{ entry.key }}</td>
							<td>
								<span v-for="value in entry.values" :key="value" class="pill">{{
									value
								}}</span>
							</td>
							<td class="note">{{ entry.note ?? "—" }}</td>
						</tr>
					</tbody>
				</table>

				<h3>Tags</h3>
				<table class="help-table">
					<thead>
						<tr>
							<th scope="col">Context</th>
							<th scope="col">Tag words</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="entry in booleanTags" :key="entry.context">
							<td class="phrase">
								<code>{{ entry.context }}</code>
							</td>
							<td>
								<template v-if="entry.tags.length">
									<span v-for="tag in entry.tags" :key="tag" class="pill">{{
										tag
									}}</span>
								</template>
								<span v-else class="note">—</span>
							</td>
						</tr>
					</tbody>
				</table>

				<h3>Address cues</h3>
				<p>
					<span v-for="cue in addressCues" :key="cue" class="pill">{{
						cue
					}}</span>
				</p>

				<h3>Free-text captures</h3>
				<p class="note">
					These keep the words after them as free text, inside their own
					context.
				</p>
				<table class="help-table">
					<thead>
						<tr>
							<th scope="col">Phrase</th>
							<th scope="col">What it captures</th>
							<th scope="col">Example utterance</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="trigger in freeTextTriggers" :key="trigger.phrase">
							<td class="phrase">{{ trigger.phrase }}</td>
							<td>{{ trigger.fires }}</td>
							<td class="example">{{ trigger.example }}</td>
						</tr>
					</tbody>
				</table>

				<h3>Counts and sizes</h3>
				<table class="help-table">
					<thead>
						<tr>
							<th scope="col">Trigger</th>
							<th scope="col">What it records</th>
							<th scope="col">Example utterance</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="trigger in countTriggers" :key="trigger.phrase">
							<td class="phrase">{{ trigger.phrase }}</td>
							<td>{{ trigger.fires }}</td>
							<td class="example">{{ trigger.example }}</td>
						</tr>
					</tbody>
				</table>
				<p class="units">
					Units:
					<span v-for="unit in units" :key="unit" class="pill">{{ unit }}</span>
				</p>
			</div>
		</details>

		<details class="reference">
			<summary>
				<h2 class="section-title">Why didn't it catch that?</h2>
			</summary>
			<div class="reference-body">
				<p class="note">
					This is the honest line between what the knowledge model declares and
					what the workspace actually reads: a vocabulary can be written down in
					the model with nothing yet wired to hear it.
				</p>
				<section
					v-for="group in gapGroups"
					:key="group.status"
					class="gap-group"
				>
					<h3>
						<span class="badge" :class="group.badge">{{ group.label }}</span>
					</h3>
					<ul class="gaps">
						<li v-for="gap in group.entries" :key="gap.area">
							<strong>{{ gap.area }}</strong>
							<span class="gap-detail">{{ gap.detail }}</span>
						</li>
					</ul>
				</section>
			</div>
		</details>

		<details class="reference">
			<summary>
				<h2 class="section-title">Using this screen</h2>
			</summary>
			<div class="reference-body">
				<h3>The controls</h3>
				<table class="help-table">
					<thead>
						<tr>
							<th scope="col">Control</th>
							<th scope="col">What it does</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="control in uiControls" :key="control.label">
							<td class="phrase">{{ control.label }}</td>
							<td>{{ control.does }}</td>
						</tr>
						<tr>
							<td class="phrase">
								<i class="bi bi-question-circle" aria-hidden="true"></i>
								Voice guide
							</td>
							<td>Opens this guide in a new window.</td>
						</tr>
					</tbody>
				</table>

				<h3>The panes</h3>
				<table class="help-table">
					<thead>
						<tr>
							<th scope="col">Pane</th>
							<th scope="col">What it shows</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="pane in panes" :key="pane.name">
							<td class="phrase">{{ pane.name }}</td>
							<td>{{ pane.shows }}</td>
						</tr>
					</tbody>
				</table>

				<h3>Colours in the transcript</h3>
				<p class="note">
					Each line of the live transcript is tinted by the context it sits in.
				</p>
				<ul class="legend">
					<li v-for="entry in contextColours" :key="entry.name">
						<span
							class="swatch"
							:style="{ backgroundColor: entry.colour }"
							aria-hidden="true"
						></span>
						<span class="legend-name">{{ entry.name }}</span>
						<code class="legend-value">{{ entry.colour }}</code>
					</li>
				</ul>

				<h3>Colours in the extractions table</h3>
				<p class="note">One row tint per fact type.</p>
				<ul class="legend">
					<li v-for="entry in rowHues" :key="entry.type">
						<span
							class="swatch"
							:style="{ backgroundColor: `hsl(${entry.hue} 70% 96%)` }"
							aria-hidden="true"
						></span>
						<span class="legend-name">{{ entry.type }}</span>
						<code class="legend-value">{{ entry.hue }}</code>
					</li>
				</ul>
				<p class="note">
					The tint behind each word box is computed from the set of keys in that
					transcript, so the same key can look different from one session to the
					next.
				</p>
			</div>
		</details>
	</div>
</template>

<style scoped>
.voice-help {
	max-width: 70rem;
}
.guide-head {
	margin-bottom: 1.25rem;
}
.guide-head h1 {
	margin: 0;
}
.purpose {
	margin: 0.35rem 0 0.5rem;
	color: #475569;
	max-width: 52rem;
}
.back-link {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	font-size: 0.9rem;
	text-decoration: none;
}
.section-title {
	margin: 1.5rem 0 0.5rem;
	font-size: 1.15rem;
}
.rule {
	margin: 0.5rem 0;
	padding: 0.6rem 0.9rem;
	border-left: 4px solid #2c7be5;
	border-radius: 0 6px 6px 0;
	background: #f1f5f9;
	font-size: 1.05rem;
	font-weight: 600;
}
.corollaries {
	margin: 0 0 0.5rem;
	padding-left: 1.2rem;
}
.corollaries li {
	margin-bottom: 0.3rem;
}
.ways {
	margin: 0 0 0.5rem;
	padding-left: 1.2rem;
}
.ways li {
	margin-bottom: 0.3rem;
}
.template {
	margin: 0.75rem 0 1rem;
	padding: 0.6rem 0.75rem;
	border: 1px solid #cbd5e1;
	border-radius: 6px;
	background: #f8fafc;
	font-family: ui-monospace, monospace;
	font-size: 0.85rem;
}
.slot {
	display: inline-block;
	margin: 0 0.2rem;
	padding: 0.05rem 0.45rem;
	border-radius: 999px;
	background: #e2e8f0;
	color: #334155;
}
.help-table {
	width: 100%;
	margin-bottom: 0.75rem;
	border-collapse: collapse;
	font-size: 0.9rem;
}
.help-table th,
.help-table td {
	padding: 0.35rem 0.5rem;
	border: 1px solid #e2e8f0;
	vertical-align: top;
	text-align: left;
}
.help-table th {
	background: #f8fafc;
	font-size: 0.8rem;
	text-transform: uppercase;
	letter-spacing: 0.02em;
	color: #64748b;
}
.phrase {
	font-weight: 600;
	white-space: nowrap;
}
.example {
	color: #334155;
	font-style: italic;
}
.pill {
	display: inline-block;
	margin: 0.1rem 0.25rem 0.1rem 0;
	padding: 0.05rem 0.45rem;
	border-radius: 999px;
	background: rgba(100, 116, 139, 0.14);
	color: #334155;
	font-size: 0.85em;
}
.units {
	margin: 0 0 0.5rem;
	font-size: 0.9rem;
	font-weight: 600;
}
.note {
	color: #64748b;
	font-size: 0.875rem;
}
.transcript {
	margin: 0.5rem 0;
}
.line {
	margin: 0.15rem 0;
	padding: 0.35rem 0.5rem;
	border-left: 3px solid #cbd5e1;
	border-radius: 4px;
}
.line.ctx-feature {
	background: #e8f5e9;
	border-left-color: #2e7d32;
}
.line.ctx-inspection {
	background: #fff8e1;
	border-left-color: #f9a825;
}
.line.ctx-treatment {
	background: #ede7f6;
	border-left-color: #5e35b1;
}
.context-tag {
	display: inline-block;
	margin-right: 0.5em;
	padding: 0 0.4em;
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.75);
	color: #475569;
	font-size: 0.7rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.03em;
}
.facts {
	margin: 0.5rem 0 0;
	padding-left: 1.2rem;
}
.facts li {
	margin-bottom: 0.35rem;
}
.reference {
	margin-top: 1rem;
	border: 1px solid #cbd5e1;
	border-radius: 6px;
	background: #fff;
}
.reference summary {
	padding: 0.5rem 0.75rem;
	cursor: pointer;
}
.reference summary .section-title {
	display: inline;
	margin: 0;
	font-size: 1.05rem;
}
.reference-body {
	padding: 0 0.75rem 0.75rem;
}
.reference-body h3 {
	margin: 1rem 0 0.35rem;
	font-size: 0.95rem;
}
.gap-group h3 {
	margin: 1rem 0 0.35rem;
}
.gaps {
	margin: 0;
	padding-left: 1.2rem;
}
.gaps li {
	margin-bottom: 0.35rem;
}
.gap-detail {
	display: block;
	color: #64748b;
	font-size: 0.875rem;
}
.legend {
	display: flex;
	flex-wrap: wrap;
	gap: 0.35rem 1rem;
	margin: 0.25rem 0 0.5rem;
	padding: 0;
	list-style: none;
	font-size: 0.875rem;
}
.legend li {
	display: flex;
	align-items: center;
	gap: 0.4rem;
}
.swatch {
	display: inline-block;
	width: 1.1rem;
	height: 1.1rem;
	border: 1px solid #cbd5e1;
	border-radius: 4px;
}
.legend-name {
	min-width: 6.5rem;
}
.legend-value {
	color: #94a3b8;
}
</style>
